/**
 * Fleet System Types (Client)
 * 
 * Type definitions for the Fleet (guild/corporation) system.
 */

export type FleetJoinPolicy = 'open' | 'invite_only';

export type FleetRank = 'admiral' | 'vice_admiral' | 'commander' | 'officer' | 'member';

export type FleetInvitationStatus = 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled';

export type FleetApplicationStatus = 'pending' | 'accepted' | 'declined' | 'withdrawn';

export type FleetUpgradeType = 
  | 'member_capacity'
  | 'territory_capacity'
  | 'treasury_capacity'
  | 'dock_discount'
  | 'repair_bonus'
  | 'trade_bonus'
  | 'defense_bonus'
  | 'siege_bonus';

export type FleetActivityType =
  | 'member_joined'
  | 'member_left'
  | 'member_kicked'
  | 'member_promoted'
  | 'member_demoted'
  | 'territory_claimed'
  | 'territory_lost'
  | 'siege_started'
  | 'siege_won'
  | 'siege_lost'
  | 'treasury_deposit'
  | 'treasury_withdraw'
  | 'upgrade_purchased'
  | 'settings_changed'
  | 'fleet_created'
  | 'fleet_disbanded'
  | 'leadership_transferred';

export interface FleetFlag {
  pattern: string;
  primaryColor: string;
  secondaryColor: string;
  emblem: string;
  emblemColor: string;
}

export interface Fleet {
  id: string;
  name: string;
  tag: string | null;
  founderId: string;
  founderName: string;
  joinPolicy: FleetJoinPolicy;
  description: string | null;
  motd: string | null;
  flag: FleetFlag;
  treasury: number;
  treasuryCapacity: number;
  level: number;
  experience: number;
  reputation: number;
  memberCount: number;
  maxMembers: number;
  maxTerritories: number;
  territoryCount: number;
  createdAt: string;
  updatedAt: string;
  disbandedAt: string | null;
}

export interface FleetMember {
  id: string;
  fleetId: string;
  characterId: string;
  characterName: string;
  rank: FleetRank;
  treasuryContributed: number;
  territoriesCaptured: number;
  joinedAt: string;
  lastActiveAt: string;
}

export interface FleetInvitation {
  id: string;
  fleetId: string;
  fleetName?: string;
  fleetTag?: string | null;
  inviterId: string;
  inviterName: string;
  inviteeId: string;
  inviteeName: string;
  status: FleetInvitationStatus;
  message: string | null;
  createdAt: string;
  expiresAt: string;
  respondedAt: string | null;
}

export interface FleetApplication {
  id: string;
  fleetId: string;
  applicantId: string;
  applicantName: string;
  applicantLevel: number;
  message: string | null;
  status: FleetApplicationStatus;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface FleetUpgrade {
  id: string;
  fleetId: string;
  upgradeType: FleetUpgradeType;
  level: number;
  purchasedAt: string;
}

export interface FleetActivityLog {
  id: string;
  fleetId: string;
  activityType: FleetActivityType;
  actorId: string | null;
  actorName: string | null;
  targetId: string | null;
  targetName: string | null;
  details: Record<string, unknown>;
  createdAt: string;
}

// Rank display info
export const RANK_DISPLAY: Record<FleetRank, { label: string; color: string; abbrev: string }> = {
  admiral: { label: 'Admiral', color: '#ffd700', abbrev: 'ADM' },
  vice_admiral: { label: 'Vice Admiral', color: '#c0c0c0', abbrev: 'VA' },
  commander: { label: 'Commander', color: '#cd7f32', abbrev: 'CMD' },
  officer: { label: 'Officer', color: '#58a6ff', abbrev: 'OFF' },
  member: { label: 'Member', color: '#8b949e', abbrev: 'MBR' },
};

// Upgrade display info
export const UPGRADE_DISPLAY: Record<FleetUpgradeType, { label: string; description: string; icon: string }> = {
  member_capacity: { label: 'Crew Quarters', description: '+5 max members per level', icon: '[+]' },
  territory_capacity: { label: 'Expansion Rights', description: '+1 max territories per level', icon: '[T]' },
  treasury_capacity: { label: 'Vault Expansion', description: '+500K treasury cap per level', icon: '[V]' },
  dock_discount: { label: 'Trade Agreements', description: '-5% dock fees per level', icon: '[D]' },
  repair_bonus: { label: 'Shipwright Guild', description: '+5% repair efficiency per level', icon: '[R]' },
  trade_bonus: { label: 'Merchant Contacts', description: '+3% trade profits per level', icon: '[M]' },
  defense_bonus: { label: 'Fortifications', description: '+5% territory defense per level', icon: '[F]' },
  siege_bonus: { label: 'Siege Engines', description: '+5% siege damage per level', icon: '[S]' },
};

// Upgrade costs by level (0-indexed, so level 1 cost is at index 0)
export const UPGRADE_COSTS: Record<FleetUpgradeType, number[]> = {
  member_capacity: [50000, 150000, 400000, 1000000, 3000000],
  territory_capacity: [100000, 300000, 750000, 2000000, 5000000],
  treasury_capacity: [25000, 75000, 200000, 500000, 1500000],
  dock_discount: [30000, 90000, 250000, 600000, 1500000],
  repair_bonus: [20000, 60000, 150000, 400000, 1000000],
  trade_bonus: [40000, 120000, 300000, 750000, 2000000],
  defense_bonus: [50000, 150000, 400000, 1000000, 2500000],
  siege_bonus: [50000, 150000, 400000, 1000000, 2500000],
};

// Fleet creation constants
export const FLEET_CREATION_COST = 100000;
export const FLEET_CREATION_MIN_LEVEL = 0;
